import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Snackbar } from '@mui/material';
import { onAuthStateChange } from '../../services/authService';
import { checkSubscriptionStatus, isPremiumSubscriptionType, normalizeSubscriptionType, onSubscriptionSnapshot } from '../../services/subscriptionService';
import type { SubscriptionCheckResult, Subscription } from '../../services/subscriptionService';
import LoginPage from '../../pages/LoginPage/LoginPage';
import { loadCachedPremium, saveCachedPremium } from '../../utils/subscriptionCache';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [showPremiumAlert, setShowPremiumAlert] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('로그인 상태를 확인하는 중...');
  
  // 리다이렉트 완료 여부 추적
  const isRedirectCompletedRef = useRef(false);
  const subscriptionUnsubscribeRef = useRef<(() => void) | null>(null);
  const backupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 대기 시간 유틸리티
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // 에러가 네트워크/Firestore 오류인지 확인
  const isNetworkError = (error: any): boolean => {
    if (!error) return false;
    
    const errorMessage = error.message?.toLowerCase() || '';
    const errorCode = error.code?.toLowerCase() || '';
    
    // Firebase/Firestore 네트워크 오류 코드들
    const networkErrorCodes = [
      'unavailable',
      'deadline-exceeded',
      'internal',
      'network-error',
      'failed-precondition',
      'aborted',
      'cancelled'
    ];
    
    const networkErrorMessages = [
      'network',
      'unavailable',
      'timeout',
      'connection',
      'fetch',
      'failed to fetch'
    ];
    
    return networkErrorCodes.some(code => errorCode.includes(code)) ||
           networkErrorMessages.some(msg => errorMessage.includes(msg));
  };

  // 구독 상태 재시도 유틸리티 (개선된 버전: 지수 백오프 + 더 긴 재시도)
  const retryCheckSubscription = async (userId: string, isAfterRedirect: boolean = false): Promise<SubscriptionCheckResult> => {
    // 리다이렉트 직후에는 초기 대기 시간 추가
    if (isAfterRedirect) {
      await sleep(1500); // 리다이렉트 직후 1.5초 대기 (Firestore 동기화 시간)
    }
    
    // 지수 백오프: [1초, 2초, 3초] = 총 최대 6초
    const delays = [1000, 2000, 3000];
    let lastError: any = null;
    
    for (let attempt = 0; attempt <= delays.length; attempt++) {
      try {
        const result = await checkSubscriptionStatus(userId);
        return result;
      } catch (e) {
        lastError = e;
        console.warn(`⚠️ AuthGuard - 구독 상태 확인 실패 (시도 ${attempt + 1}/${delays.length + 1}):`, e);
        
        // 네트워크 오류가 아니면 재시도하지 않음
        if (!isNetworkError(e)) {
          console.error('❌ AuthGuard - 네트워크 오류가 아닌 에러:', e);
          throw e;
        }
        
        // 마지막 시도면 에러 throw
        if (attempt === delays.length) {
          throw e;
        }
        
        // 다음 재시도 전 대기
        await sleep(delays[attempt]);
      }
    }
    
    // 논리적으로 도달하지 않지만 타입 안전을 위해
    throw lastError || new Error('구독 상태 확인 실패');
  };

  // 구독 상태를 실시간 리스너로 확인
  const setupSubscriptionListener = (userId: string, isInitialCheck: boolean = false) => {
    // 기존 리스너 해제
    if (subscriptionUnsubscribeRef.current) {
      subscriptionUnsubscribeRef.current();
      subscriptionUnsubscribeRef.current = null;
    }

    
    if (isInitialCheck) {
      setLoadingMessage('구독 정보를 확인하는 중...');
    }

    // 초기 체크인 경우 타임아웃 설정 (최대 3초 대기)
    let timeoutId: NodeJS.Timeout | null = null;
    if (isInitialCheck) {
      timeoutId = setTimeout(() => {
        console.warn('⚠️ AuthGuard - 실시간 리스너 타임아웃, 백업 로직 대기 중...');
        // 타임아웃이 발생해도 여기서는 로딩 해제하지 않음 (백업 로직이 처리)
      }, 3000);
    }

    // 실시간 리스너 설정
    const unsubscribe = onSubscriptionSnapshot(userId, (subscription: Subscription | null) => {
      
      // 타임아웃 취소
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      if (subscription) {
        const now = new Date();
        const isExpired = subscription.endDate ? subscription.endDate < now : false;
        const status = subscription.status || 'ACTIVE';
        const hasAccess = !isExpired && status === 'ACTIVE';
        // 구독 타입 정규화 (대소문자 구분 없이)
        const normalizedType = normalizeSubscriptionType(subscription.type);
        const hasPremiumAccess = hasAccess && isPremiumSubscriptionType(normalizedType);
        saveCachedPremium(userId, hasPremiumAccess);


        // 초기 체크인 경우에만 상태 업데이트 (기존 로직과 충돌 방지)
        if (isInitialCheck) {
          setIsPremium(hasPremiumAccess);

          // 프리미엄(PREMIUM/SERVICE_PREMIUM)만 대시보드 진입 허용
          if (hasPremiumAccess) {
            if (location.pathname === '/login') {
              navigate('/', { replace: true });
            }
          } else {
            setShowPremiumAlert(true);
            if (location.pathname !== '/login') {
              navigate('/login', { replace: true });
            }
          }

          setIsLoading(false);
        } else {
          // 이후 변경사항만 업데이트 (이미 로딩이 완료된 경우)
          if (!isLoading) {
            setIsPremium(hasPremiumAccess);
            if (!hasPremiumAccess && location.pathname !== '/login') {
              setShowPremiumAlert(true);
              navigate('/login', { replace: true });
            }
          }
        }
      } else {
        // 구독이 없는 경우
        saveCachedPremium(userId, false);

        // 초기 체크인 경우에만 상태 업데이트
        if (isInitialCheck) {
          setIsPremium(false);
          setShowPremiumAlert(true);
          if (location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
          setIsLoading(false);
        }
      }
    });

    subscriptionUnsubscribeRef.current = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      unsubscribe();
    };
  };

  useEffect(() => {
    // Apple 로그인 리다이렉트 결과 처리 (페이지 로드 시 한 번만)
    const handleRedirectResult = async () => {
      try {
        const { handleAppleRedirectResult } = await import('@/services/authService');
        const result = await handleAppleRedirectResult();
        if (result) {
        }
      } catch (error) {
        // 리다이렉트 결과가 없거나 이미 처리된 경우는 무시
        if (error && typeof error === 'object' && 'code' in error) {
          const authError = error as any;
          if (authError.code !== 'auth/operation-not-allowed') {
            console.error('ℹ️ AuthGuard - 리다이렉트 결과 없음 (정상):', authError.code);
          }
        }
      }
    };
    
    handleRedirectResult();
    
    const unsubscribe = onAuthStateChange(async (user) => {
      setIsAuthenticated(!!user);
      setLoadingMessage('로그인 상태를 확인하는 중...');
      
      if (user) {
        // 로그인된 경우
        const isAfterRedirect = !isRedirectCompletedRef.current;
        isRedirectCompletedRef.current = true;
        
        try {
          // 리다이렉트 직후인지 확인 (URL에 관련 파라미터가 있는지 체크)
          const urlParams = new URLSearchParams(window.location.search);
          const isAppleRedirect = urlParams.has('code') || urlParams.has('id_token') || window.location.hash.includes('apple');
          
          // 리다이렉트 URL 정리 (사용자 경험 개선)
          if (isAppleRedirect) {
            // URL에서 인증 관련 파라미터 제거
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          }
          
          // Apple 리다이렉트인 경우 특별 처리
          if (isAppleRedirect && isAfterRedirect) {
            setLoadingMessage('구독 정보를 동기화하는 중...');
            
            // 실시간 리스너로 구독 상태 확인 (더 안정적)
            setupSubscriptionListener(user.uid, true);
            
            // 백업으로 재시도 로직도 실행 (실시간 리스너가 느릴 경우 대비)
            // 타임아웃: 최대 5초 후에는 백업 로직으로 처리
            // 기존 타임아웃 정리
            if (backupTimeoutRef.current) {
              clearTimeout(backupTimeoutRef.current);
            }
            
            backupTimeoutRef.current = setTimeout(async () => {
              try {
                const result = await retryCheckSubscription(user.uid, true);
                // 백업 로직이 실행되면 무조건 로딩 해제
                saveCachedPremium(user.uid, result.hasPremiumAccess);
                setIsPremium(result.hasPremiumAccess);

                // 프리미엄(PREMIUM/SERVICE_PREMIUM)만 대시보드 진입 허용
                if (result.hasPremiumAccess) {
                  if (location.pathname === '/login') {
                    navigate('/', { replace: true });
                  }
                } else {
                  setShowPremiumAlert(true);
                  if (location.pathname !== '/login') {
                    navigate('/login', { replace: true });
                  }
                }

                setIsLoading(false);
              } catch (error) {
                console.warn('⚠️ AuthGuard - 백업 구독 확인 실패:', error);
                // 에러가 발생해도 로딩 해제 (무한 로딩 방지)
                setIsPremium(false);
                setIsLoading(false);
                if (location.pathname !== '/login') {
                  navigate('/login', { replace: true });
                }
              }
            }, 5000); // 5초로 증가
          } else if (loadCachedPremium(user.uid)) {
            // ── 빠른 진입 경로 — 최근 프리미엄 확인된 계정은 로딩 없이 즉시 렌더,
            //    구독 상태는 백그라운드에서 재검증 (만료/해지 시에만 차단)
            setIsPremium(true);
            setIsLoading(false);
            if (location.pathname === '/login') {
              navigate('/', { replace: true });
            }
            setupSubscriptionListener(user.uid, false);

            retryCheckSubscription(user.uid, false)
              .then((result) => {
                saveCachedPremium(user.uid, result.hasPremiumAccess);
                if (!result.hasPremiumAccess) {
                  setIsPremium(false);
                  setShowPremiumAlert(true);
                  if (location.pathname !== '/login') {
                    navigate('/login', { replace: true });
                  }
                }
              })
              .catch((error) => {
                // 네트워크 오류 — 캐시 유지, 실시간 리스너에 의존
                console.warn('⚠️ AuthGuard - 백그라운드 구독 재검증 실패:', error);
              });
          } else {
            // 일반 로그인 (구글 등) - 기존 로직대로 즉시 확인
            setLoadingMessage('구독 정보를 확인하는 중...');

            try {
              const subscriptionResult = await retryCheckSubscription(user.uid, false);
              saveCachedPremium(user.uid, subscriptionResult.hasPremiumAccess);
              setIsPremium(subscriptionResult.hasPremiumAccess);

              // 프리미엄(PREMIUM/SERVICE_PREMIUM)만 대시보드 진입 허용
              if (subscriptionResult.hasPremiumAccess) {
                if (location.pathname === '/login') {
                  navigate('/', { replace: true });
                }
              } else {
                setShowPremiumAlert(true);
                if (location.pathname !== '/login') {
                  navigate('/login', { replace: true });
                }
              }

              setIsLoading(false);
              
              // 구독 상태 확인 완료 후 실시간 리스너 설정 (향후 변경사항 감지용)
              // isInitialCheck=false로 설정하여 이미 확인된 상태를 덮어쓰지 않음
              setupSubscriptionListener(user.uid, false);
            } catch (error) {
              console.error('❌ AuthGuard - 구독 상태 확인 실패:', error);
              
              // 네트워크 오류가 아닌 경우에만 로그인 페이지로 이동
              if (!isNetworkError(error)) {
                setIsPremium(false);
                if (location.pathname !== '/login') {
                  navigate('/login', { replace: true });
                }
                setIsLoading(false);
              } else {
                // 네트워크 오류는 실시간 리스너에 의존
                console.error('🌐 AuthGuard - 네트워크 오류, 실시간 리스너에 의존');
                setupSubscriptionListener(user.uid);
              }
            }
          }
        } catch (error) {
          console.error('❌ AuthGuard - 예상치 못한 오류:', error);
          setIsPremium(false);
          setIsLoading(false);
        }
      } else {
        // 로그인되지 않은 경우
        setIsPremium(false);
        isRedirectCompletedRef.current = false;
        
        // 실시간 리스너 해제
        if (subscriptionUnsubscribeRef.current) {
          subscriptionUnsubscribeRef.current();
          subscriptionUnsubscribeRef.current = null;
        }
        
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
        setIsLoading(false);
      }
    });

    return () => {
      unsubscribe();
      // 컴포넌트 언마운트 시 실시간 리스너도 해제
      if (subscriptionUnsubscribeRef.current) {
        subscriptionUnsubscribeRef.current();
        subscriptionUnsubscribeRef.current = null;
      }
      // 백업 타임아웃 정리
      if (backupTimeoutRef.current) {
        clearTimeout(backupTimeoutRef.current);
        backupTimeoutRef.current = null;
      }
    };
  }, [navigate, location.pathname]);

  // 로딩 중
  if (isLoading) {
    return (
        <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--bg-base)',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'var(--primary)', mb: 2 }} />
        <Typography variant="h6" sx={{ color: 'var(--fg-strong)', fontWeight: 500 }}>
          {loadingMessage}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--fg-tertiary)', mt: 1 }}>
          잠시만 기다려 주세요
        </Typography>
      </Box>
    );
  }

  // 비로그인·비프리미엄은 LoginPage, 프리미엄만 자식 렌더링.
  // 프리미엄 안내 스낵바는 FREE 사용자가 로그인 화면에 머물 때 보여야 하므로 분기 밖에서 렌더링한다.
  const content = !isAuthenticated || !isPremium ? <LoginPage /> : children;

  return (
    <>
      {content}

      {/* 프리미엄 결제 안내 스낵바 */}
      <Snackbar
        open={showPremiumAlert}
        autoHideDuration={8000}
        onClose={() => setShowPremiumAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="warning" 
          onClose={() => setShowPremiumAlert(false)}
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              fontSize: '1rem',
              fontWeight: 600
            }
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            🌱 프리미엄 회원이 필요합니다
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            웹 대시보드 이용을 위해 일기월장 앱에서 프리미엄 결제를 진행해 주세요.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            앱에서 결제 완료 후 다시 로그인해 주세요.
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

export default AuthGuard;
